/**
 * The board this device is looking at, kept in step with everyone else's.
 *
 * The rule from docs/03-ARCHITECTURE.md §7.2 holds here too: ink is already on screen
 * before this file hears about it. Everything below happens after the stroke has been
 * drawn, and nothing in the drawing path ever waits for it.
 *
 * A stroke finished before the board has finished opening is held and sent the moment
 * it can be — a teacher who starts writing immediately must not lose that first line.
 */

import {
  findPage,
  fromStoredStroke,
  getObjects,
  getPages,
  getStrokes,
  listObjects,
  putObject,
  putStroke,
  type StoredStroke,
} from '@opal/board-doc';
import {
  ACCESS_STATES,
  accessMessageId,
  type BoardAccessState,
  type BoardObject,
  type Layer,
  type Stroke,
  type Unsubscribe,
} from '@opal/shared';
import type { Awareness } from 'y-protocols/awareness';
import { WebsocketProvider } from 'y-websocket';
import * as Y from 'yjs';

import { applyEdit, type BoardEdit, type EditTarget } from './board-edit.js';
import type { InkSession } from './ink-session.js';

/** What the person should be told about their connection (SRS D5 builds on this). */
export type ConnectionStatus = 'connecting' | 'connected' | 'offline';

/** The board itself, without any opinion about how it reaches other people. */
export interface BoardBridge {
  readonly doc: Y.Doc;
  /** The page being worked on, or null until the board has arrived. */
  pageId(): string | null;
  /**
   * SRS C1 — turns to another page of the same board.
   *
   * Everything on the page being left stays exactly where it is: a page is put down,
   * never closed. Listeners are told before the new page's work arrives, so the screen
   * can empty itself first and the two pages never appear on top of one another.
   */
  setPage(pageId: string): void;
  /** Calls back whenever the page being worked on changes. */
  onPageChanged(handler: (pageId: string) => void): Unsubscribe;
  /** Shares a stroke that was just drawn here. */
  commit(stroke: Stroke): void;
  /**
   * SRS C4 - changes work already on the board: erased, moved, resized or recoloured.
   *
   * One step, whatever it touched: a teacher who wipes a corner of the board or drags
   * a whole diagram gets it back with one press of undo, not one press per line.
   */
  edit(edit: BoardEdit): void;
  /** Calls back with each stroke drawn by somebody else. */
  onRemoteStroke(handler: (stroke: Stroke) => void): Unsubscribe;
  /** Calls back when a stroke is taken off the board, by anybody. */
  onStrokeRemoved(handler: (strokeId: string) => void): Unsubscribe;
  /**
   * SRS C4 - calls back with every object on the page whenever any of them changes.
   *
   * The whole list rather than the difference: a page carries tens of objects, not
   * the thousands of strokes a long lesson leaves, so comparing the list is cheaper
   * to do than a stream of changes is to get right.
   */
  onObjectsChanged(handler: (objects: readonly BoardObject[]) => void): Unsubscribe;
  /**
   * SRS C6 - takes back the last thing THIS person did, and nothing anybody else did.
   *
   * Returns false when there is nothing of their own left to undo, which is what
   * stops a teacher's undo from reaching into a student's work.
   */
  undo(): boolean;
  redo(): boolean;
  destroy(): void;
}

export interface BoardSync extends BoardBridge {
  /** Who else is here and where their hands are. Never saved (SRS D2). */
  readonly awareness: Awareness;
  status(): ConnectionStatus;
  onStatus(handler: (status: ConnectionStatus) => void): Unsubscribe;
  /** SRS D4 — the server saying what this person may do. Arrives on joining too. */
  onAccess(handler: (access: BoardAccessState) => void): Unsubscribe;
  /** Stops trying to reconnect. Used when the server says this person is no longer in. */
  disconnect(): void;
  /** Tries again now, rather than waiting for the next attempt (docs/06-UI-SPEC.md §17.3). */
  reconnect(): void;
}

/** Who this browser is claiming to be. A student carries the place they were given. */
export interface BoardCredentials {
  readonly participantId: string | null;
}

/** Where the sync server is, in development and in production alike. */
export function syncUrl(location: { protocol: string; host: string }): string {
  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/sync`;
}

/**
 * Marks a change as this person's own.
 *
 * SRS C6 undo is per user, and this is what tells the two apart: only changes carrying
 * this origin go on this device's undo pile. A stroke arriving from somebody else
 * carries the connection as its origin instead, and can never be undone from here.
 */
export const LOCAL_ORIGIN = 'local';

interface BridgeState {
  readonly doc: Y.Doc;
  /** Strokes drawn before the board arrived, waiting for a page to go on. */
  readonly waiting: Stroke[];
  /** Shapes drawn before the board arrived, waiting for the same thing. */
  readonly waitingObjects: BoardObject[];
  /** Ids of strokes drawn here, so our own work is never handed back as remote. */
  readonly mine: Set<string>;
  /** Every stroke id this device has seen on the page, so it can spot one leaving. */
  readonly known: Set<string>;
  readonly handlers: Set<(stroke: Stroke) => void>;
  readonly removalHandlers: Set<(strokeId: string) => void>;
  readonly objectHandlers: Set<(objects: readonly BoardObject[]) => void>;
  readonly pageHandlers: Set<(pageId: string) => void>;
  pageId: string | null;
  unobserve: (() => void) | null;
  /** SRS C6 - this person's own pile of changes, and nobody else's. */
  undoManager: Y.UndoManager | null;
  /**
   * One pile per page, kept for as long as the board is open.
   *
   * SRS C6 asks for unlimited undo within a session. A teacher who turns to page 2
   * and back must still be able to take back what they did on page 1, so the pile is
   * put down with the page rather than thrown away.
   */
  readonly undoManagers: Map<string, Y.UndoManager>;
}

function firstPageId(doc: Y.Doc): string | null {
  const pages = getPages(doc).toArray();
  const first = pages[0];
  return first ? ((first.get('id') as string | undefined) ?? null) : null;
}

function announce(state: BridgeState, stored: StoredStroke): void {
  state.known.add(stored.id);
  if (state.mine.has(stored.id)) return;
  const stroke = fromStoredStroke(stored);
  for (const handler of state.handlers) handler(stroke);
}

/**
 * Works out what has gone from the page and says so.
 *
 * Read from what the page holds now rather than from the change itself: once an item
 * has gone, its contents are not reliably still there to be read.
 */
function announceRemovals(state: BridgeState, strokes: Y.Array<StoredStroke>): void {
  const present = new Set(strokes.toArray().map((stored) => stored.id));
  for (const id of [...state.known]) {
    if (present.has(id)) continue;
    state.known.delete(id);
    state.mine.delete(id);
    for (const handler of state.removalHandlers) handler(id);
  }
}

/** Says what the page's objects are now, to anybody watching. */
function announceObjects(state: BridgeState): void {
  const pageId = state.pageId;
  if (!pageId) return;
  const objects = listObjects(state.doc, pageId);
  for (const handler of state.objectHandlers) handler(objects);
}

/** Watches the page's work and reports anything that did not come from this device. */
function observePage(state: BridgeState, pageId: string): void {
  const page = findPage(state.doc, pageId);
  if (!page) return;
  const strokes = getStrokes(page);
  const objects = getObjects(page);

  const onChange = (event: Y.YArrayEvent<StoredStroke>): void => {
    for (const item of event.changes.added) {
      for (const stored of item.content.getContent() as StoredStroke[]) {
        announce(state, stored);
      }
    }
    if (event.changes.deleted.size > 0) announceRemovals(state, strokes);
  };

  // Deep, because an object is a map of its own: somebody dragging a shape changes
  // what is inside it rather than the list holding it.
  const onObjects = (): void => {
    announceObjects(state);
  };

  strokes.observe(onChange);
  objects.observeDeep(onObjects);
  state.unobserve = () => {
    strokes.unobserve(onChange);
    objects.unobserveDeep(onObjects);
  };

  // SRS C6 - unlimited within a session, and each stroke its own step: a teacher who
  // presses undo twice expects two lines gone, not everything drawn in the last
  // half second. Shapes go on the same pile, so undo takes back the last thing done
  // whichever tool did it.
  const kept = state.undoManagers.get(pageId);
  state.undoManager =
    kept ??
    new Y.UndoManager([strokes, objects], {
      trackedOrigins: new Set([LOCAL_ORIGIN]),
      captureTimeout: 0,
    });
  state.undoManagers.set(pageId, state.undoManager);
}

/** Takes the board on: sends what was waiting, shows what is already there. */
function adopt(state: BridgeState, pageId: string): void {
  state.pageId = pageId;
  observePage(state, pageId);
  // Said before anything else, so the screen knows which page it is looking at from
  // the moment the board arrives rather than only when one is turned.
  for (const handler of state.pageHandlers) handler(pageId);

  for (const stroke of state.waiting) {
    state.mine.add(stroke.id);
    putStroke(state.doc, pageId, stroke, LOCAL_ORIGIN);
  }
  state.waiting.length = 0;
  for (const object of state.waitingObjects) {
    putObject(state.doc, pageId, object, LOCAL_ORIGIN);
  }
  state.waitingObjects.length = 0;

  showPage(state, pageId);
}

/** Hands the screen everything the page already holds. */
function showPage(state: BridgeState, pageId: string): void {
  const page = findPage(state.doc, pageId);
  if (!page) return;
  for (const stored of getStrokes(page).toArray()) announce(state, stored);
  announceObjects(state);
}

/**
 * Turns to another page (SRS C1).
 *
 * The order matters: the screen is told the page has changed BEFORE the new page's
 * work is handed to it, so it can clear the last page off the canvas first.
 */
function turnTo(state: BridgeState, pageId: string): void {
  if (state.pageId === pageId || !findPage(state.doc, pageId)) return;
  state.unobserve?.();
  state.unobserve = null;
  // Kept per page: what this screen has seen, and what it drew itself, are both
  // questions about one page and neither carries over to the next.
  state.known.clear();
  state.mine.clear();
  state.pageId = pageId;

  for (const handler of state.pageHandlers) handler(pageId);
  observePage(state, pageId);
  showPage(state, pageId);
}

/** Picks the page up as soon as the board arrives. */
function watchForPage(state: BridgeState): Unsubscribe {
  const check = (): void => {
    if (state.pageId) return;
    const pageId = firstPageId(state.doc);
    if (pageId) adopt(state, pageId);
  };

  const pages = getPages(state.doc);
  pages.observe(check);
  check();
  return () => {
    pages.unobserve(check);
  };
}

/** Steps this person's own pile one way or the other. False when there is none left. */
function undoStep(state: BridgeState, direction: 'undo' | 'redo'): boolean {
  const manager = state.undoManager;
  if (!manager) return false;
  if (direction === 'undo') {
    if (!manager.canUndo()) return false;
    manager.undo();
    return true;
  }
  if (!manager.canRedo()) return false;
  manager.redo();
  return true;
}

/** How the bridge presents itself to the part that writes a change. */
function editTarget(state: BridgeState): EditTarget {
  return {
    doc: state.doc,
    pageId: state.pageId,
    waiting: state.waiting,
    waitingObjects: state.waitingObjects,
    mine: state.mine,
    origin: LOCAL_ORIGIN,
  };
}

/** SRS C1 — which page is being worked on, and turning to another. */
function paging(state: BridgeState): Pick<BoardBridge, 'pageId' | 'setPage' | 'onPageChanged'> {
  return {
    pageId: () => state.pageId,
    setPage: (pageId) => {
      turnTo(state, pageId);
    },
    onPageChanged: (handler) => {
      state.pageHandlers.add(handler);
      return () => {
        state.pageHandlers.delete(handler);
      };
    },
  };
}

/** Everything a board screen can ask to be told about. */
function listeners(state: BridgeState): Pick<
  BoardBridge,
  'onRemoteStroke' | 'onStrokeRemoved' | 'onObjectsChanged'
> {
  return {
    onRemoteStroke: (handler) => {
      state.handlers.add(handler);
      return () => {
        state.handlers.delete(handler);
      };
    },
    onStrokeRemoved: (handler) => {
      state.removalHandlers.add(handler);
      return () => {
        state.removalHandlers.delete(handler);
      };
    },
    onObjectsChanged: (handler) => {
      state.objectHandlers.add(handler);
      // What is already on the page, so a screen that arrives late is not empty.
      if (state.pageId) handler(listObjects(state.doc, state.pageId));
      return () => {
        state.objectHandlers.delete(handler);
      };
    },
  };
}

/**
 * Wraps a board document so ink can be put on it and taken off it.
 *
 * Separate from the connection so the behaviour that matters — nothing lost, nothing
 * echoed back to the person who drew it — can be tested without a server.
 */
export function attachToDoc(doc: Y.Doc): BoardBridge {
  const state: BridgeState = {
    doc,
    waiting: [],
    waitingObjects: [],
    mine: new Set<string>(),
    known: new Set<string>(),
    handlers: new Set<(stroke: Stroke) => void>(),
    removalHandlers: new Set<(strokeId: string) => void>(),
    objectHandlers: new Set<(objects: readonly BoardObject[]) => void>(),
    pageHandlers: new Set<(pageId: string) => void>(),
    pageId: null,
    unobserve: null,
    undoManager: null,
    undoManagers: new Map<string, Y.UndoManager>(),
  };

  const stopWatching = watchForPage(state);

  return {
    doc,
    ...paging(state),
    commit: (stroke) => {
      const pageId = state.pageId;
      if (!pageId) {
        state.waiting.push(stroke);
        return;
      }
      state.mine.add(stroke.id);
      putStroke(doc, pageId, stroke, LOCAL_ORIGIN);
    },
    edit: (edit) => {
      applyEdit(editTarget(state), edit);
    },
    ...listeners(state),
    undo: () => undoStep(state, 'undo'),
    redo: () => undoStep(state, 'redo'),
    destroy: () => {
      stopWatching();
      state.unobserve?.();
      for (const manager of state.undoManagers.values()) manager.destroy();
      state.undoManagers.clear();
      state.undoManager = null;
    },
  };
}

/**
 * Joins the ink on this device to the shared board, both ways.
 *
 * Local strokes go out after they are already on screen; strokes from other people
 * come in and land exactly as if they had been drawn here.
 */
export function bindInkToBoard(
  ink: InkSession,
  bridge: BoardBridge,
  /** SRS C8 — false for work on a layer this person may not see. */
  visible: (work: { readonly layer: Layer }) => boolean = () => true,
): Unsubscribe {
  const stopSending = ink.onFinished((stroke) => {
    bridge.commit(stroke);
  });
  const stopReceiving = bridge.onRemoteStroke((stroke) => {
    // SRS C8 — the teacher-only layer never reaches a student's board at all. Not
    // drawn and then hidden: never taken in, so there is nothing on this screen to
    // find, to erase, to select, or to put in an export.
    if (!visible(stroke)) {
      ink.remove(stroke.id);
      return;
    }
    // A stroke this screen already holds has been changed rather than drawn — moved,
    // resized or recoloured by somebody else. It has to be swapped, not added, or the
    // two screens would disagree about where the work is.
    if (!ink.replace(stroke)) ink.accept(stroke);
  });
  // An undo anywhere takes that line off every screen, including this one.
  const stopRemovals = bridge.onStrokeRemoved((strokeId) => {
    ink.remove(strokeId);
  });
  return () => {
    stopSending();
    stopReceiving();
    stopRemovals();
  };
}

/**
 * Listens for the server's word on what this person may do.
 *
 * The library keeps a slot per message number, so these ride the same connection the
 * board does rather than needing a second one open per person.
 */
function listenForAccess(
  provider: WebsocketProvider,
  handlers: Set<(access: BoardAccessState) => void>,
): void {
  for (const access of ACCESS_STATES) {
    provider.messageHandlers[accessMessageId(access)] = () => {
      for (const handler of handlers) handler(access);
    };
  }
}

/** Opens the shared copy of a board and keeps it in step with the server. */
export function connectBoard(
  boardId: string,
  serverUrl: string,
  credentials: BoardCredentials = { participantId: null },
): BoardSync {
  const doc = new Y.Doc();
  const bridge = attachToDoc(doc);
  const provider = new WebsocketProvider(serverUrl, boardId, doc, {
    // The student's place in the class. The server checks it before sending anything.
    params: credentials.participantId ? { pid: credentials.participantId } : {},
  });

  const statusHandlers = new Set<(status: ConnectionStatus) => void>();
  const accessHandlers = new Set<(access: BoardAccessState) => void>();
  let status: ConnectionStatus = 'connecting';

  provider.on('status', (event: { status: string }) => {
    const next: ConnectionStatus = event.status === 'connected' ? 'connected' : 'offline';
    if (next === status) return;
    status = next;
    for (const handler of statusHandlers) handler(next);
  });

  listenForAccess(provider, accessHandlers);

  return {
    ...bridge,
    awareness: provider.awareness,
    status: () => status,
    onStatus: (handler) => {
      statusHandlers.add(handler);
      return () => {
        statusHandlers.delete(handler);
      };
    },
    onAccess: (handler) => {
      accessHandlers.add(handler);
      return () => {
        accessHandlers.delete(handler);
      };
    },
    disconnect: () => {
      provider.disconnect();
    },
    reconnect: () => {
      provider.connect();
    },
    destroy: () => {
      bridge.destroy();
      provider.destroy();
      doc.destroy();
    },
  };
}
